import { useCallback, useEffect, useState } from "react";

import { ApiError, festivals } from "./api";

/**
 * Dépôt du fond aérien (CARTOGRAPHIE.md, niveau C).
 *
 * L'archive est produite hors ligne par `tiles/scripts/orthophoto-to-pmtiles.sh`
 * — le traitement photogrammétrique n'a pas sa place dans un navigateur — et
 * seulement déposée ici.
 */

const MB = 1_000_000;

type State = { present: boolean; bytes?: number; minZoom?: number; maxZoom?: number };

export function OrthophotoEditor({ slug }: { slug: string }) {
  const [state, setState] = useState<State>({ present: false });
  const [progress, setProgress] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setState(await festivals.orthophoto(slug));
    } catch {
      setMessage("Lecture impossible.");
    }
  }, [slug]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const upload = async (file: File) => {
    setMessage(null);
    setProgress(0);
    try {
      const result = await festivals.uploadOrthophoto(slug, file, setProgress);
      setState({ present: true, ...result });
      setMessage(
        `Fond aérien déposé : ${(result.bytes / MB).toFixed(0)} Mo, jusqu'au zoom ${result.maxZoom}.`,
      );
    } catch (cause) {
      setMessage(
        cause instanceof ApiError && cause.status === 415
          ? "Cette archive ne contient pas d'images. Vérifiez qu'elle sort bien du script de traitement."
          : cause instanceof ApiError && cause.status === 413
            ? "Archive trop volumineuse."
            : "Dépôt impossible.",
      );
    } finally {
      setProgress(null);
    }
  };

  return (
    <section className="card">
      <h2>Fond aérien</h2>
      <p className="muted">
        L'orthophoto issue du vol, transformée en archive par{" "}
        <code>tiles/scripts/orthophoto-to-pmtiles.sh</code>. Elle s'affiche sous le plan
        dans l'application — et n'y est téléchargée que si le festivalier le demande,
        son poids étant annoncé.
      </p>

      {state.present ? (
        <>
          <p className="badge published">
            {`${((state.bytes ?? 0) / MB).toFixed(0)} Mo · zoom ${state.minZoom} à ${state.maxZoom}`}
          </p>
          <div className="row">
            <button
              className="danger"
              onClick={async () => {
                await festivals.deleteOrthophoto(slug);
                setState({ present: false });
                setMessage("Fond aérien retiré.");
              }}
            >
              Retirer le fond aérien
            </button>
          </div>
        </>
      ) : (
        <p className="muted">Aucun fond aérien pour ce festival.</p>
      )}

      <label>
        Archive .pmtiles
        <input
          type="file"
          accept=".pmtiles,application/vnd.pmtiles,application/octet-stream"
          disabled={progress !== null}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void upload(file);
          }}
        />
      </label>

      {progress !== null ? (
        <>
          {/* Une barre, et pas seulement un message : sans elle, un envoi de
              deux cents mégaoctets passe pour un plantage. */}
          <div className="upload-gauge" aria-hidden="true">
            <div
              className="upload-gauge-fill"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <p className="muted" aria-live="polite">
            Envoi en cours — {(progress * 100).toFixed(0)} %
          </p>
        </>
      ) : null}

      {message ? <p className="muted">{message}</p> : null}
    </section>
  );
}
